import { useFeature } from '../auth/features';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Fingerprint, Search, RefreshCw, ChevronRight, ChevronDown,
    Loader2, Link2, Layers, Database, Crosshair, Send, X, ArrowRight, ScanSearch,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
    CategoryIconTile,
    CopyIconButton,
    IntelStatIcon,
    SeverityBadge,
    type IntelStatTone,
} from '../components/intel';
import {
    mulehunterService, caseService, instagramOsintService, identityService,
    type MulehunterCaseScan, type MulehunterCaseMatch, type MulehunterCasePromote,
    type MulehunterP2PPromote, type MulehunterTelegramPromote,
} from '../services/api';
import PhoneInventoryPanel from '../components/mulehunter/PhoneInventoryPanel';
import PhoneOsintPanel from '../components/mulehunter/PhoneOsintPanel';
import BatchScreenPanel from '../components/mulehunter/BatchScreenPanel';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import { useSerialQueries } from '../hooks/useSerialQueries';

interface CaseLite { id: string; title: string }
type SourcePromotionResult = {
    source: 'P2P' | 'Telegram' | 'Instagram';
    caseId: string;
    scanned: number;
    entities: number;
    edges: number;
};

// ── Types (mirror the /api/v1/mulehunter response shapes) ──────────────────
interface Identifier {
    id: number;
    id_type: string;
    value: string;
    raw_value: string | null;
    chain: string | null;
    occurrences: number;
    source_count: number;
    sources: string[];
    cross_source: boolean;
    entity_name: string | null;
    entity_category: string | null;
    risk_hint: string | null;
    // Phone country (backend phone_country.py). Optional: absent on
    // non-phones and on a backend that has not run the phone-region rollup.
    phone_region?: string | null;
    phone_region_confidence?: PhoneRegionConfidence | null;
    phone_e164?: string | null;
    first_seen: string | null;
    last_seen: string | null;
}

type PhoneRegionConfidence = 'explicit' | 'context' | 'context_weak' | 'assumed' | 'unknown';

interface Observation {
    source_class: string;
    source_table: string;
    source_ref: string;
    context: string | null;
    observed_at: string | null;
}

interface Dossier {
    identifier: Identifier;
    observations: Observation[];
    linked: { type: string; value: string }[];
}

interface Stats {
    totals: {
        identifiers: number;
        // Three grains of corroboration. cross_source counts distinct source
        // CLASSES and reads near-zero because one class (telegram) carries
        // almost every observation; multi_origin is the figure that answers
        // "was this posted in more than one place?". See backend
        // services/mulehunter/rollup.py.
        cross_source: number;
        multi_surface?: number;
        multi_origin?: number;
        observations: number;
    };
    by_type: { id_type: string; count: number }[];
    by_source: { source_class: string; count: number }[];
    by_risk: { risk: string; count: number }[];
}

// utr and pan were reachable only through the API; both now exist in the index
// (UTRs from cued text and image OCR, PAN from OCR'd slips).
const ID_TYPES = ['', 'upi', 'phone', 'wallet', 'handle', 'email', 'bank', 'ifsc', 'utr', 'pan', 'domain', 'tx_hash', 'url'];
const SOURCES = ['', 'telegram', 'p2p', 'instagram'];
const RISKS = ['', 'high', 'medium', 'low', 'phishing', 'unrated'];
const ORDERS = [
    { value: 'recent', label: 'Most recent' },
    { value: 'occurrences', label: 'Most mentions' },
    { value: 'cross_source', label: 'Most source types first' },
];
const PAGE_SIZE = 50;

const SOURCE_STYLE: Record<string, string> = {
    telegram: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
    p2p: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
    instagram: 'bg-primary/10 text-foreground border-primary/20',
};

function fmtDate(d: string | null): string {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return d; }
}

function Chip({ className, children }: { className?: string; children: ReactNode }) {
    return (
        <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full border', className)}>
            {children}
        </span>
    );
}

export default function HarvestFeed() {
    const [stats, setStats] = useState<Stats | null>(null);
    const [items, setItems] = useState<Identifier[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [showScan, setShowScan] = useState(false);
    const [showScreen, setShowScreen] = useState(false);

    // filters
    const [idType, setIdType] = useState('');
    const [source, setSource] = useState('');
    const [risk, setRisk] = useState('');
    const [crossOnly, setCrossOnly] = useState(false);
    const [q, setQ] = useState('');
    const [order, setOrder] = useState('recent');
    const [page, setPage] = useState(0);

    // row expansion + dossier cache
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [dossiers, setDossiers] = useState<Record<number, Dossier | 'loading'>>({});

    // Sources/admin panel endpoints are require_admin on the API.
    const isAdmin = useFeature('admin.tools');

    const listParamsRef = useRef({ idType, source, risk, q, crossOnly, order, page });
    listParamsRef.current = { idType, source, risk, q, crossOnly, order, page };
    const countAbortRef = useRef<AbortController | null>(null);

    const widgets = useSerialQueries<any>([
        {
            key: 'stats',
            timeout: 60_000,
            fn: async (signal) => mulehunterService.stats({ signal, timeout: 60_000 }),
        },
        {
            key: 'list',
            timeout: 15_000,
            fn: async (signal) => {
                const p = listParamsRef.current;
                return mulehunterService.listIdentifiers({
                    id_type: p.idType || undefined,
                    source: p.source || undefined,
                    risk: p.risk || undefined,
                    q: p.q.trim() || undefined,
                    min_sources: p.crossOnly ? 2 : 1,
                    order: p.order,
                    limit: PAGE_SIZE,
                    offset: p.page * PAGE_SIZE,
                }, { signal, timeout: 15_000 });
            },
        },
    ], { concurrency: 2 });

    useEffect(() => {
        if (widgets.results.stats) setStats(widgets.results.stats);
    }, [widgets.results.stats]);

    useEffect(() => {
        const res = widgets.results.list;
        if (!res) return;
        setItems(res.items || []);
        if (typeof res.total === 'number') {
            setTotal(res.total);
            return;
        }
        countAbortRef.current?.abort();
        const ac = new AbortController();
        countAbortRef.current = ac;
        const p = listParamsRef.current;
        mulehunterService.countIdentifiers({
            id_type: p.idType || undefined,
            source: p.source || undefined,
            risk: p.risk || undefined,
            q: p.q.trim() || undefined,
            min_sources: p.crossOnly ? 2 : 1,
        }, { signal: ac.signal, timeout: 60_000 }).then((c) => {
            if (!ac.signal.aborted) setTotal(c.total ?? 0);
        }).catch(() => { /* count is chrome */ });
    }, [widgets.results.list]);

    useEffect(() => {
        const listStatus = widgets.statuses.list;
        if (listStatus === 'loading') {
            if (items.length === 0) setLoading(true);
            else setRefreshing(true);
        } else {
            setLoading(false);
            setRefreshing(false);
        }
    }, [widgets.statuses.list, items.length]);

    const skipListRetryRef = useRef(true);
    useEffect(() => {
        if (skipListRetryRef.current) {
            skipListRetryRef.current = false;
            return;
        }
        widgets.retry('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idType, source, risk, q, crossOnly, order, page]);

    const loadStats = useCallback(async (fresh = false) => {
        try {
            setStats(await mulehunterService.stats({ fresh, timeout: 60_000 }));
        } catch (e) {
            console.error('harvest stats failed', e);
        }
    }, []);

    const loadList = useCallback(() => {
        widgets.retry('list');
    }, [widgets.retry]);

    useEffect(() => { setPage(0); setExpandedId(null); }, [idType, source, risk, q, crossOnly, order]);

    const toggleRow = async (row: Identifier) => {
        if (expandedId === row.id) { setExpandedId(null); return; }
        setExpandedId(row.id);
        if (!dossiers[row.id]) {
            setDossiers(prev => ({ ...prev, [row.id]: 'loading' }));
            try {
                const d = await mulehunterService.getIdentifier(row.id);
                setDossiers(prev => ({ ...prev, [row.id]: d }));
            } catch (e) {
                console.error('dossier failed', e);
                setDossiers(prev => { const c = { ...prev }; delete c[row.id]; return c; });
                toast.error('Failed to load identifier detail');
            }
        }
    };

    const copy = (v: string) => {
        navigator.clipboard?.writeText(v).then(
            () => toast.success('Copied'),
            () => toast.error('Copy failed'),
        );
    };

    const rebuild = async () => {
        setRebuilding(true);
        try {
            await mulehunterService.runBackfill();
            toast.loading('Rebuilding index…', { id: 'mh-rebuild' });
            // Poll status to completion.
            const poll = async (): Promise<void> => {
                const st = await mulehunterService.backfillStatus();
                if (st.state === 'running') { await new Promise(r => setTimeout(r, 2500)); return poll(); }
                if (st.state === 'error') { throw new Error(st.error || 'backfill error'); }
            };
            await poll();
            toast.success('Index rebuilt', { id: 'mh-rebuild' });
            await Promise.all([loadStats(true), Promise.resolve(loadList())]);
        } catch (e: any) {
            console.error('rebuild failed', e);
            toast.error(e?.response?.status === 403 ? 'Admin access required' : 'Rebuild failed', { id: 'mh-rebuild' });
        } finally {
            setRebuilding(false);
        }
    };

    const knownTotal = total ?? (items.length ? page * PAGE_SIZE + items.length : 0);
    const from = knownTotal === 0 ? 0 : page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, knownTotal);
    const listBusy = loading && items.length === 0;

    return (
        <div className={`p-6 max-w-7xl mx-auto space-y-6`}>
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                        <Fingerprint className="w-5 h-5 text-primary" /> Harvest Feed
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                        Unified index of scam identifiers harvested from Telegram, P2P exchanges, Instagram OSINT,
                        threat feeds and FlowGuard off-ramp checks — deduplicated, with occurrence counts per origin.
                        Browsing this page changes nothing; an Off-Ramp check adds the VPA/account it queried.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowScan(v => !v)}
                        className={clsx(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors border',
                            showScan
                                ? 'bg-primary/10 border-primary/30 text-primary'
                                : 'border-border text-foreground hover:bg-accent',
                        )}
                        title="Match a case's identifiers against this index and promote the hits into its graph"
                    >
                        <Crosshair className="w-4 h-4" /> Scan a case
                    </button>
                    <button
                        onClick={() => setShowScreen(v => !v)}
                        className={clsx(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors border',
                            showScreen
                                ? 'bg-primary/10 border-primary/30 text-primary'
                                : 'border-border text-foreground hover:bg-accent',
                        )}
                        title="Paste a list of VPAs, phones, IFSCs, UTRs or wallets and see which appear in this index, with PSP / bank / rail attribution"
                    >
                        <ScanSearch className="w-4 h-4" /> Screen a batch
                    </button>
                    <button
                        onClick={() => { loadStats(true); loadList(); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    {isAdmin && (
                        <button
                            onClick={rebuild}
                            disabled={rebuilding}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm hover:opacity-90 disabled:opacity-60 transition"
                        >
                            {rebuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                            {rebuilding ? 'Rebuilding…' : 'Rebuild index'}
                        </button>
                    )}
                </div>
            </div>

            {/* Case scan panel */}
            {showScan && <CaseScanPanel onClose={() => setShowScan(false)} />}

            {/* Batch screen panel (POST /mulehunter/screen) */}
            {showScreen && <BatchScreenPanel onClose={() => setShowScreen(false)} />}

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Identifiers" value={stats?.totals.identifiers} icon={Fingerprint} />
                <StatCard
                    label="Corroborated"
                    value={stats?.totals.multi_origin ?? stats?.totals.cross_source}
                    icon={Link2}
                    hint="Observed at 2+ independent origins — a different Telegram channel, advertiser or OSINT run"
                    sub={
                        stats
                            ? `${stats.totals.multi_surface ?? 0} across extraction surfaces · ${stats.totals.cross_source} across source types`
                            : undefined
                    }
                    emphasize
                    tone="brand"
                />
                <StatCard label="Observations" value={stats?.totals.observations} icon={Layers} />
                <StatCard
                    label="Sources"
                    value={stats?.by_source.length}
                    icon={Database}
                    sub={stats?.by_source.map(s => `${s.source_class} ${s.count}`).join(' · ')}
                />
            </div>

            <PhoneInventoryPanel />

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search value (UPI, phone, wallet, domain…)"
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
                <Select value={idType} onChange={setIdType} options={ID_TYPES} labelAll="All types" />
                <Select value={source} onChange={setSource} options={SOURCES} labelAll="All sources" />
                <Select value={risk} onChange={setRisk} options={RISKS} labelAll="All risk" />
                <select
                    value={order}
                    onChange={e => setOrder(e.target.value)}
                    className="px-2.5 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
                >
                    {ORDERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border border-border bg-background text-foreground cursor-pointer select-none"
                    title="Only identifiers seen in 2+ source types (e.g. Telegram AND P2P). Stricter than the Corroborated card, which counts 2+ independent origins such as two different Telegram channels."
                >
                    <input type="checkbox" checked={crossOnly} onChange={e => setCrossOnly(e.target.checked)} className="accent-primary" />
                    <Link2 className="w-3.5 h-3.5" /> 2+ source types only
                </label>
            </div>

            {/* Table */}
            <div className="border border-border rounded-lg overflow-hidden bg-card">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                            <th className="w-8"></th>
                            <th className="px-3 py-2 font-medium">Type</th>
                            <th className="px-3 py-2 font-medium">Value</th>
                            <th className="px-3 py-2 font-medium">Sources</th>
                            <th className="px-3 py-2 font-medium text-right">Mentions</th>
                            <th className="px-3 py-2 font-medium">Risk</th>
                            <th className="px-3 py-2 font-medium">Attribution</th>
                            <th className="px-3 py-2 font-medium">Region</th>
                            <th className="px-3 py-2 font-medium">Last seen</th>
                        </tr>
                    </thead>
                    <tbody className={refreshing ? 'opacity-70' : undefined}>
                        {listBusy ? (
                            <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
                            </td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">
                                No identifiers match these filters.
                            </td></tr>
                        ) : items.map(row => (
                            <FeedRow
                                key={row.id}
                                row={row}
                                expanded={expandedId === row.id}
                                dossier={dossiers[row.id]}
                                onToggle={() => toggleRow(row)}
                                onCopy={copy}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{from}–{to} of {total == null ? '…' : total.toLocaleString('en-IN')}</span>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="px-3 py-1.5 rounded-md border border-border hover:bg-accent disabled:opacity-40 text-foreground"
                    >Prev</button>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={total == null ? items.length < PAGE_SIZE : to >= total}
                        className="px-3 py-1.5 rounded-md border border-border hover:bg-accent disabled:opacity-40 text-foreground"
                    >Next</button>
                </div>
            </div>
        </div>
    );
}

// ── Case scan: match a case's identifiers against the index + promote to graph ──
function CaseScanPanel({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchString = searchParams.toString();
    const urlCaseId = searchParams.get('case_id') || '';
    const [cases, setCases] = useState<CaseLite[]>([]);
    const [caseId, setCaseId] = useState('');
    const [scan, setScan] = useState<MulehunterCaseScan | null>(null);
    const [scanning, setScanning] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [result, setResult] = useState<MulehunterCasePromote | null>(null);
    const [sourcePromoting, setSourcePromoting] = useState<'p2p' | 'telegram' | 'instagram' | null>(null);
    const [sourceResult, setSourceResult] = useState<SourcePromotionResult | null>(null);

    useEffect(() => {
        let cancelled = false;
        caseService.getAll().then((d: any) => {
            if (cancelled) return;
            const list: CaseLite[] = Array.isArray(d) ? d : (d?.cases || d?.data || []);
            setCases(list);
        }).catch(() => {
            if (!cancelled) setCases([]);
        });
        return () => { cancelled = true; };
    }, []);

    // Keep this page anchored to the active case while preserving other URL context.
    useEffect(() => {
        if (!cases.length) {
            setCaseId('');
            return;
        }
        const nextCaseId = cases.some(c => c.id === urlCaseId) ? urlCaseId : cases[0].id;
        setCaseId(nextCaseId);
        if (nextCaseId !== urlCaseId) {
            const nextParams = new URLSearchParams(searchString);
            nextParams.set('case_id', nextCaseId);
            setSearchParams(nextParams, { replace: true });
        }
    }, [cases, searchString, setSearchParams, urlCaseId]);

    // A scan/result only describes the currently-selected case — clear on change.
    useEffect(() => {
        setScan(null);
        setResult(null);
        setSourceResult(null);
    }, [caseId]);

    const selectCase = (nextCaseId: string) => {
        setCaseId(nextCaseId);
        const nextParams = new URLSearchParams(searchString);
        if (nextCaseId) nextParams.set('case_id', nextCaseId);
        else nextParams.delete('case_id');
        setSearchParams(nextParams, { replace: true });
    };

    const runScan = async () => {
        if (!caseId) return;
        setScanning(true); setResult(null);
        try {
            setScan(await mulehunterService.scanCase(caseId));
        } catch {
            toast.error('Scan failed');
        } finally {
            setScanning(false);
        }
    };

    const promote = async () => {
        if (!caseId) return;
        setPromoting(true);
        try {
            const r = await mulehunterService.promoteCase(caseId);
            setResult(r);
            toast.success(`Matched ${r.matched} · ${r.leads} leads · ${r.edges} links`);
        } catch {
            toast.error('Promote failed');
        } finally {
            setPromoting(false);
        }
    };

    const promoteSource = async (source: 'p2p' | 'telegram' | 'instagram') => {
        if (!caseId) return;
        const targetCaseId = caseId;
        setSourcePromoting(source);
        setSourceResult(null);
        try {
            if (source === 'instagram') {
                const response = await instagramOsintService.promoteToGraph(targetCaseId);
                setSourceResult({
                    source: 'Instagram',
                    caseId: targetCaseId,
                    scanned: response.profiles ?? 0,
                    entities: response.entities,
                    edges: response.edges,
                });
                toast.success('Instagram harvest promoted to case graph');
            } else {
                const response: MulehunterP2PPromote | MulehunterTelegramPromote = source === 'p2p'
                    ? await mulehunterService.promoteP2P(targetCaseId, { platform: 'all' })
                    : await mulehunterService.promoteTelegram(targetCaseId);
                if (response.status !== 'ok') {
                    throw new Error(response.error || `${source} promotion failed`);
                }
                const scanned = source === 'p2p'
                    ? (response as MulehunterP2PPromote).ads_scanned
                    : (response as MulehunterTelegramPromote).messages_scanned;
                setSourceResult({
                    source: source === 'p2p' ? 'P2P' : 'Telegram',
                    caseId: targetCaseId,
                    scanned,
                    entities: response.entities,
                    edges: response.edges,
                });
                toast.success(`${source === 'p2p' ? 'P2P' : 'Telegram'} intelligence promoted`);
            }
        } catch {
            const label = source === 'p2p' ? 'P2P' : source === 'telegram' ? 'Telegram' : 'Instagram';
            toast.error(`${label} promotion failed`);
        } finally {
            setSourcePromoting(null);
        }
    };

    const matches = scan?.matches || [];
    const busy = scanning || promoting || sourcePromoting !== null;

    return (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <Crosshair className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground">Scan a case against the harvest index</h2>
                <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground" title="Close">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <p className="text-xs text-muted-foreground max-w-3xl">
                Finds the case's own identifiers (wallets / UPI / phones / handles) that also appear in harvested scam
                data, then promotes the hits — plus identifiers seen alongside them in the same scam message / P2P ad —
                into the case graph as scored leads.
            </p>

            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={caseId}
                    onChange={e => selectCase(e.target.value)}
                    disabled={busy}
                    aria-label="Case for harvest promotion"
                    className="px-2.5 py-1.5 text-sm rounded-md border border-border bg-background text-foreground max-w-[320px]"
                >
                    {cases.length === 0 && <option value="">No cases</option>}
                    {cases.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
                <button
                    onClick={runScan}
                    disabled={!caseId || scanning}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
                >
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />} Scan
                </button>
                <button
                    onClick={() => promoteSource('p2p')}
                    disabled={!caseId || busy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
                    title="Promote recent Binance, OKX, Bybit, and HTX advertisement identifiers into this case"
                >
                    {sourcePromoting === 'p2p' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Promote P2P ads
                </button>
                <button
                    onClick={() => promoteSource('telegram')}
                    disabled={!caseId || busy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
                    title="Promote recent Telegram wallets, VPAs, channels, and sender handles into this case"
                >
                    {sourcePromoting === 'telegram' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Promote Telegram intel
                </button>
                <button
                    onClick={() => promoteSource('instagram')}
                    disabled={!caseId || busy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
                    title="Promote this case's harvested Instagram profiles and artefacts into the case graph"
                >
                    {sourcePromoting === 'instagram' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Promote Instagram harvest
                </button>
                {scan && matches.length > 0 && (
                    <button
                        onClick={promote}
                        disabled={promoting}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm hover:opacity-90 disabled:opacity-60"
                        title="Write matches + co-occurrence leads into the case graph (scored, tier-3)"
                    >
                        {promoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Promote to case graph
                    </button>
                )}
                {scan && <span className="text-xs text-muted-foreground">{matches.length} match{matches.length === 1 ? '' : 'es'}</span>}
            </div>

            {result && (
                <div className="flex items-center gap-2 text-xs rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-700 dark:text-emerald-300">
                    <span>Matched <b>{result.matched}</b> · <b>{result.leads}</b> co-occurrence leads · <b>{result.edges}</b> links written to the case graph.</span>
                    <button onClick={() => navigate(`/cases/${caseId}/canvas`)} className="ml-auto inline-flex items-center gap-1 font-semibold hover:underline">
                        Open Fusion Canvas <ArrowRight className="w-3 h-3" />
                    </button>
                </div>
            )}

            {sourceResult && (
                <div className="flex items-center gap-2 text-xs rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-700 dark:text-emerald-300">
                    <span>
                        {sourceResult.source}: scanned <b>{sourceResult.scanned}</b> records · promoted <b>{sourceResult.entities}</b> entities · wrote <b>{sourceResult.edges}</b> links.
                    </span>
                    <button
                        onClick={() => navigate(`/cases/${sourceResult.caseId}/canvas`)}
                        className="ml-auto inline-flex items-center gap-1 font-semibold hover:underline"
                    >
                        Open Fusion Canvas <ArrowRight className="w-3 h-3" />
                    </button>
                </div>
            )}

            {scan && (
                matches.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                        None of this case's identifiers appear in the harvest index yet. Add identifiers first
                        (Instagram OSINT, bank import, or Fusion intake), then scan again.
                    </div>
                ) : (
                    <div className="border border-border rounded-md overflow-hidden bg-card">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">Type</th>
                                    <th className="px-3 py-2 font-medium">Value</th>
                                    <th className="px-3 py-2 font-medium">Seen in</th>
                                    <th className="px-3 py-2 font-medium text-right">Mentions</th>
                                    <th className="px-3 py-2 font-medium">Risk</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matches.map((m: MulehunterCaseMatch) => (
                                    <tr key={m.entity_id} className="border-b border-border last:border-0">
                                        <td className="px-3 py-1.5"><Chip className="bg-muted text-muted-foreground border-border">{m.type}</Chip></td>
                                        <td className="px-3 py-1.5 font-mono text-xs text-foreground max-w-[280px] truncate" title={m.value}>{m.label || m.value}</td>
                                        <td className="px-3 py-1.5">
                                            <div className="flex flex-wrap gap-1">
                                                {m.sources.map(s => <Chip key={s} className={SOURCE_STYLE[s] || 'bg-muted text-muted-foreground border-border'}>{s}</Chip>)}
                                                {m.cross_source && <Chip className="bg-primary/10 text-primary border-primary/30"><span className="inline-flex items-center gap-1" title="Seen in 2+ source types"><Link2 className="w-3 h-3" />cross</span></Chip>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-foreground">{m.occurrences.toLocaleString('en-IN')}</td>
                                        <td className="px-3 py-1.5">
                                            {m.risk_hint
                                                ? <SeverityBadge level={m.risk_hint === 'phishing' ? 'critical' : m.risk_hint} label={m.risk_hint} />
                                                : <span className="text-muted-foreground text-xs">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}

function StatCard({ label, value, icon, hint, sub, emphasize, tone = 'neutral' }: {
    label: string; value?: number; icon: LucideIcon; hint?: string; sub?: string; emphasize?: boolean; tone?: IntelStatTone;
}) {
    return (
        <div className={clsx('rounded-lg border p-3', emphasize ? 'border-primary/30 bg-primary/5' : 'border-border bg-card')}>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <IntelStatIcon icon={icon} tone={tone} />
                {label}
            </div>
            <div className="text-2xl font-bold text-foreground mt-1">
                {value === undefined ? '—' : value.toLocaleString('en-IN')}
            </div>
            {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
            {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate" title={sub}>{sub}</div>}
        </div>
    );
}

function Select({ value, onChange, options, labelAll }: {
    value: string; onChange: (v: string) => void; options: string[]; labelAll: string;
}) {
    return (
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className="px-2.5 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
        >
            {options.map(o => <option key={o} value={o}>{o === '' ? labelAll : o}</option>)}
        </select>
    );
}

/** Email identifiers: one explicit live check (HIBP + local breach pivots). */
function EmailOsintCheck({ email }: { email: string }) {
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState<{ summary?: string[] } | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const emailRef = useRef(email);
    emailRef.current = email;
    useEffect(() => { setRes(null); setErr(null); setBusy(false); }, [email]);
    const run = async () => {
        const requested = email;
        setBusy(true); setErr(null);
        try {
            const r = await identityService.enrich(requested, 'email');
            if (emailRef.current === requested) setRes(r);
        } catch {
            if (emailRef.current === requested) setErr('Live check failed');
        } finally {
            if (emailRef.current === requested) setBusy(false);
        }
    };
    return (
        <div className="text-xs flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={run} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-card hover:bg-accent/40 disabled:opacity-60">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Fingerprint className="w-3 h-3" />} Live OSINT check
            </button>
            {err && <span className="text-red-400">{err}</span>}
            {res && <span className="text-muted-foreground">{res.summary?.length ? res.summary.join(' · ') : 'No breach hits'}</span>}
        </div>
    );
}

/** Country of a phone identifier. A bare 10-digit number is shape-identical to
 *  an Indian mobile whether or not it is one, so "IN (assumed)" must read as a
 *  presumption, not a finding -- hence the muted suffix. */
const REGION_QUALIFIER: Partial<Record<PhoneRegionConfidence, string>> = {
    assumed: 'assumed',
    context_weak: 'weak',
};

function PhoneRegionBadge({ row }: { row: Identifier }) {
    if (row.id_type !== 'phone' || !row.phone_region) {
        return <span className="text-muted-foreground">—</span>;
    }
    const conf = row.phone_region_confidence ?? null;
    const qualifier = conf ? REGION_QUALIFIER[conf] : undefined;
    return (
        <span title={[row.phone_e164, conf].filter(Boolean).join(' · ')}>
            <span className="font-mono text-foreground">{row.phone_region}</span>
            {qualifier && <span className="text-muted-foreground"> ({qualifier})</span>}
        </span>
    );
}

function FeedRow({ row, expanded, dossier, onToggle, onCopy }: {
    row: Identifier;
    expanded: boolean;
    dossier: Dossier | 'loading' | undefined;
    onToggle: () => void;
    onCopy: (v: string) => void;
}) {
    return (
        <>
            <tr
                onClick={onToggle}
                className={clsx('border-b border-border cursor-pointer hover:bg-accent/40 transition-colors', expanded && 'bg-accent/30')}
            >
                <td className="pl-2 text-muted-foreground">
                    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </td>
                <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                        <CategoryIconTile idType={row.id_type} size="sm" />
                        <Chip className="bg-muted text-muted-foreground border-border">{row.id_type}</Chip>
                    </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-foreground max-w-[280px]">
                    <div className="group flex items-center gap-0.5">
                        <span className="truncate" title={row.value}>{row.value}</span>
                        <CopyIconButton
                            value={row.value}
                            onCopy={(v, e) => { e.stopPropagation(); onCopy(v); }}
                            revealOnGroup
                        />
                    </div>
                </td>
                <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                        {row.sources.map(s => (
                            <Chip key={s} className={SOURCE_STYLE[s] || 'bg-muted text-muted-foreground border-border'}>{s}</Chip>
                        ))}
                        {row.cross_source && (
                            <Chip className="bg-primary/10 text-primary border-primary/30"><span className="inline-flex items-center gap-1" title="Seen in 2+ source types"><Link2 className="w-3 h-3" />cross</span></Chip>
                        )}
                    </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">{row.occurrences.toLocaleString('en-IN')}</td>
                <td className="px-3 py-2">
                    {row.risk_hint
                        ? <SeverityBadge level={row.risk_hint === 'phishing' ? 'critical' : row.risk_hint} label={row.risk_hint} />
                        : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-foreground max-w-[180px] truncate" title={row.entity_name || ''}>
                    {row.entity_name
                        ? <span>{row.entity_name}{row.entity_category ? <span className="text-muted-foreground"> · {row.entity_category}</span> : null}</span>
                        : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                    <PhoneRegionBadge row={row} />
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(row.last_seen)}</td>
            </tr>
            {expanded && (
                <tr className="border-b border-border bg-muted/30">
                    <td colSpan={9} className="px-6 py-4">
                        {dossier === 'loading' || !dossier ? (
                            <div className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading detail…</div>
                        ) : (
                            <div className="space-y-4">
                                {dossier.linked.length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Linked identifiers</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {dossier.linked.map((l, i) => (
                                                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border border-border bg-card font-mono">
                                                    <span className="text-muted-foreground">{l.type}</span> {l.value}
                                                    <CopyIconButton
                                                        value={l.value}
                                                        onCopy={(_, e) => { e.stopPropagation(); onCopy(l.value); }}
                                                        revealOnGroup={false}
                                                        className="h-6 w-6 opacity-100"
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {row.id_type === 'phone' && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">OSINT</div>
                                        <PhoneOsintPanel phone={row.value} />
                                    </div>
                                )}
                                {row.id_type === 'email' && <EmailOsintCheck email={row.value} />}
                                <div>
                                    <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">
                                        Occurrences ({dossier.observations.length})
                                    </div>
                                    <div className="space-y-1.5 max-h-72 overflow-auto">
                                        {dossier.observations.map((o, i) => (
                                            <div key={i} className="flex items-start gap-2 text-xs border border-border rounded-md bg-card px-2.5 py-1.5">
                                                <Chip className={SOURCE_STYLE[o.source_class] || 'bg-muted text-muted-foreground border-border'}>{o.source_class}</Chip>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-muted-foreground">
                                                        <span className="font-mono">{o.source_table}</span> · <span className="font-mono">{o.source_ref}</span> · {fmtDate(o.observed_at)}
                                                    </div>
                                                    {o.context && <div className="text-foreground/80 italic mt-0.5 line-clamp-2">“{o.context}”</div>}
                                                </div>
                                            </div>
                                        ))}
                                        {dossier.observations.length === 0 && (
                                            <div className="text-muted-foreground">No stored context.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}
